import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Trash2, Send, Trophy, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { Avatar, MemberChip } from "./Bits";
import { inputCls } from "./JoinSteps";

const ago = (iso, lang) => new Date(iso).toLocaleString(lang === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export const Composer = ({ onPosted }) => {
  const { tr } = useLang();
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [cardId, setCardId] = useState("");
  const [cards, setCards] = useState([]);
  useEffect(() => { api.get("/collection").then((r) => setCards(r.data)).catch(() => setCards([])); }, []);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post("/feed", { text, card_id: cardId || undefined }); setText(""); setCardId(""); toast.success(data.points ? `+${data.points} ${tr("Punkte", "points")}` : tr("Gepostet!", "Posted!")); onPosted(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-[#120E1F]/5 p-4 space-y-3" data-testid="feed-composer">
      <div className="flex gap-3"><Avatar id={user.avatar} />
        <textarea required maxLength={500} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={tr("Teile deinen neuesten Pull, eine Frage oder dein Sammler-Highlight…", "Share your latest pull, a question or a collector highlight…")} data-testid="feed-text-input" className={`${inputCls} flex-1`} /></div>
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <select value={cardId} onChange={(e) => setCardId(e.target.value)} data-testid="feed-card-select" className="rounded-full border border-[#120E1F]/10 px-3 py-1.5 text-xs max-w-[60%]">
          <option value="">{tr("Karte aus Sammlung anhängen (optional)", "Attach card from collection (optional)")}</option>
          {cards.map((c) => <option key={c.card_id} value={c.card_id}>{`${c.name} · ${c.set_name}`}</option>)}
        </select>
        <button className="btn-gold" data-testid="feed-post-btn"><Send className="h-4 w-4" />{tr("Posten", "Post")}</button>
      </div>
    </form>
  );
};

const Comments = ({ post, onCount }) => {
  const { tr, lang } = useLang();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  useEffect(() => { api.get(`/feed/${post.id}/comments`).then((r) => setList(r.data)); }, [post.id]);
  const send = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/feed/${post.id}/comments`, { text }); setList([...list, { ...data, author: { display_name: user.display_name, avatar: user.avatar } }]); setText(""); onCount(1); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="border-t pt-3 space-y-3" data-testid={`comments-${post.id}`}>
      {list.map((c) => <div key={c.id} className="flex gap-2 text-sm"><Avatar id={c.author?.avatar} size="h-7 w-7" ring={false} /><div className="bg-[#F7F5FB] rounded-xl px-3 py-2"><span className="font-semibold text-xs">{c.author?.display_name}</span> <span className="text-[10px] text-[#707783]">{ago(c.created_at, lang)}</span><p>{c.text}</p></div></div>)}
      {user && <form onSubmit={send} className="flex gap-2"><input required maxLength={300} value={text} onChange={(e) => setText(e.target.value)} placeholder={tr("Kommentieren…", "Write a comment…")} data-testid={`comment-input-${post.id}`} className="flex-1 rounded-full border border-[#120E1F]/10 px-4 py-2 text-sm" /><button className="btn-dark !px-4" data-testid={`comment-send-${post.id}`}><Send className="h-3.5 w-3.5" /></button></form>}
    </div>
  );
};

const PostCard = ({ p, reload }) => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [liked, setLiked] = useState(p.liked);
  const [likes, setLikes] = useState(p.like_count);
  const [count, setCount] = useState(p.comment_count);
  const [open, setOpen] = useState(false);
  const like = async () => {
    if (!user) return toast.info("Login");
    const { data } = await api.post(`/feed/${p.id}/like`); setLiked(data.liked); setLikes(data.like_count);
  };
  const del = async () => { await api.delete(`/feed/${p.id}`); reload(); };
  const mine = user && (user.id === p.user_id || user.role === "admin");
  return (
    <article className="bg-white rounded-2xl border border-[#120E1F]/5 p-5 space-y-3 fade-up" data-testid={`post-${p.id}`}>
      <div className="flex justify-between items-start"><MemberChip author={p.author} /><span className="text-[11px] text-[#707783]">{ago(p.created_at, lang)}</span></div>
      <p className="text-sm text-[#120E1F] whitespace-pre-line">{p.text}</p>
      {p.card && <div className="flex gap-4 items-center rounded-xl bg-[#120E1F] p-3"><img src={p.card.image_small} alt={p.card.name} className="h-28 rounded-md" loading="lazy" /><div><p className="text-white font-semibold">{p.card.name}</p><p className="text-xs text-[#C9B6F2]">{p.card.set_name} · #{p.card.number}</p><p className="text-[11px] text-[#F2C230] mt-1">{p.card.rarity}</p></div></div>}
      <div className="flex items-center gap-5 text-sm text-[#707783]">
        <button onClick={like} className={`flex items-center gap-1.5 transition-colors ${liked ? "text-[#E0457B]" : "hover:text-[#E0457B]"}`} data-testid={`like-btn-${p.id}`}><Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} /><span data-testid={`like-count-${p.id}`}>{likes}</span></button>
        <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 hover:text-[#6E3FB8]" data-testid={`comments-toggle-${p.id}`}><MessageCircle className="h-4 w-4" />{count}</button>
        {mine && <button onClick={del} className="ml-auto hover:text-red-600" data-testid={`delete-post-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
      </div>
      {open && <Comments post={p} onCount={(n) => setCount(count + n)} />}
    </article>
  );
};

export const Feed = () => {
  const { user } = useAuth();
  const { tr } = useLang();
  const [posts, setPosts] = useState(null);
  const reload = useCallback(() => api.get("/feed").then((r) => setPosts(r.data)), []);
  useEffect(() => { reload(); }, [reload, user]);
  return (
    <div className="space-y-4" data-testid="community-feed">
      {user && user.onboarded && <Composer onPosted={reload} />}
      {user && !user.onboarded && <Link to="/join" className="block rounded-2xl bg-[#8A5CD6]/10 p-4 text-sm text-[#6E3FB8]">{tr("Vervollständige dein Profil, um zu posten →", "Complete your profile to post →")}</Link>}
      {posts && posts.length === 0 && <p className="text-[#707783] text-sm" data-testid="feed-empty">{tr("Noch keine Beiträge – sei der/die Erste!", "No posts yet – be the first!")}</p>}
      {(posts || []).map((p) => <PostCard key={p.id} p={p} reload={reload} />)}
    </div>
  );
};

export const Leaderboard = () => {
  const { tr } = useLang();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/community/leaderboard").then((r) => setList(r.data)); }, []);
  return (
    <div className="bg-[#120E1F] rounded-2xl p-5 space-y-4" data-testid="leaderboard">
      <h3 className="font-display text-2xl text-white flex items-center gap-2"><Trophy className="h-5 w-5 text-[#F2C230]" />{tr("Bestenliste", "Leaderboard")}</h3>
      {list.length === 0 && <p className="text-sm text-white/50">{tr("Noch leer.", "Empty so far.")}</p>}
      <ol className="space-y-3">
        {list.map((m, i) => (
          <li key={m.id} data-testid={`leader-${i}`}><Link to={`/u/${m.display_name}`} className="flex items-center gap-3 group">
            <span className={`w-6 text-center font-mono text-sm ${i < 3 ? "text-[#F2C230]" : "text-white/40"}`}>{i + 1}</span>
            <Avatar id={m.avatar} size="h-9 w-9" ring={i < 3} />
            <span className="flex-1 min-w-0"><span className="block text-sm text-white truncate group-hover:text-[#F2C230] transition-colors">{m.display_name}</span><span className="text-[10px] text-white/50 flex items-center gap-1"><Layers className="h-3 w-3" />{m.cards} · Lv {m.level}</span></span>
            <span className="font-mono text-xs text-[#C9B6F2]">{m.xp} XP</span>
          </Link></li>
        ))}
      </ol>
    </div>
  );
};
